import { FEATURE_FLAG, NEW_BOARD_KILL_SWITCH, ROLE } from '@/shared/constant';
import { NEW_BOARD_KEYS } from '@/shared/lib';
import { attendanceLoader } from '@/shared/loader';
import { AppLayout, NavbarLayout } from '@/shared/ui';

import { CheckExamPeriodRoute } from '@/feature/exam/lib';
import {
  inquiryEditLoader,
  reportEditLoader,
  validateReportWriteLoader,
} from '@/feature/support/loader';
import SupportUpdateErrorPage from '@/feature/support/SupportUpdateErrorPage';

import {
  FindIdPage,
  FindPwPage,
  FoundIdPage,
  FoundPwPage,
  LoginPage,
  NotFoundIdPage,
  NotFoundPwPage,
  SignUpFailurePage,
  SignUpPage,
  SignUpSuccessPage,
  SnoroseVerifyPage,
} from '@/page/account';
import { AlertPage, AlertSettingPage, MarketingTermsPage } from '@/page/alert';
import {
  BoardCategoryPage,
  EditPostPage,
  NoticeListPage,
  PostDetailPage,
  PostListPage,
  WritePostPage,
} from '@/page/board';
import OrderDetailPage from '@/page/commerce/OrderDetailPage';
import OrderListPage from '@/page/commerce/OrderListPage';
import PickupDisplayPage from '@/page/commerce/PickupDisplayPage';
import SaleDetailPage from '@/page/commerce/SaleDetailPage';
import { ErrorPage, NotFoundPage } from '@/page/etc';
import {
  EditEventPage,
  EventListPage,
  EventPage,
  WriteEventPage,
} from '@/page/event';
import {
  EditExamReviewPage,
  ExamReviewListPage,
  ExamReviewPage,
  WriteExamReviewPage,
} from '@/page/exam';
import { AttendancePage, MainPage } from '@/page/home';
import { MaintenancePage } from '@/page/maintenance';
import { SearchPage } from '@/page/search';
import {
  AboutPage,
  PrivacyPolicyPage,
  ServicePolicyPage,
} from '@/page/snorose';
import {
  EditInquiryPage,
  EditReportPage,
  FAQPage,
  InquiryDetailPage,
  ReportDetailPage,
  WriteInquiryPage,
  WriteReportPage,
} from '@/page/support';
import {
  ActivityPage,
  ChangePwPage,
  DeleteAccountPage,
  EditProfilePage,
  MyPage,
  PointLogListPage,
} from '@/page/user';

import App from '@/App';
import FeatureGuard from '@/FeatureGuard';
import LegacyBoardRedirect from '@/LegacyBoardRedirect';
import ProtectedRoute from '@/ProtectedRoute';

const getRolesForReadBoard = (boardPath) => {
  switch (boardPath) {
    // 강등·광고주까지 열람 가능 (준회원·정회원·리자·공식계정·강등·광고주)
    case 'notice':
      return [
        ROLE.preUser,
        ROLE.user,
        ROLE.admin,
        ROLE.official,
        ROLE.blacklist,
        ROLE.advertiser,
      ];
    // 준회원부터 열람 가능 (준회원·정회원·리자·공식계정)
    case 'first-snow':
    case 'all':
      return [ROLE.preUser, ROLE.user, ROLE.admin, ROLE.official];
    // 정회원부터 열람 가능 (정회원·리자·공식계정)
    case 'large-snow':
    case 'permanent-snow':
    case 'besookt':
    case 'sookplace':
    case 'student-council':
    case 'graduation-preparation':
    case 'finance-audit':
    case 'residence':
    case 'video-content':
    case 'anime-comics':
    case 'book':
    case 'music':
    case 'performance':
    case 'cooking':
    case 'sport':
    case 'exhibition':
    case 'study-abroad':
    case 'trip':
    case 'pet':
    case 'health':
    case 'beauty':
    case 'game':
    case 'finance':
    case 'certification':
    case 'club':
    case 'promotion':
    case 'living':
    case 'women-studies':
      return [ROLE.user, ROLE.admin, ROLE.official];
    // 그 외 경로는 아무도 열람할 수 없음
    default:
      return [];
  }
};

const getRolesForWriteBoard = (boardPath) => {
  switch (boardPath) {
    // 준회원부터 작성 가능 (준회원·정회원·리자·공식계정)
    case 'first-snow':
      return [ROLE.preUser, ROLE.user, ROLE.admin, ROLE.official];
    // 정회원부터 작성 가능 (정회원·리자·공식계정)
    case 'large-snow':
    case 'permanent-snow':
    case 'sookplace':
    case 'residence':
    case 'video-content':
    case 'anime-comics':
    case 'book':
    case 'music':
    case 'performance':
    case 'cooking':
    case 'sport':
    case 'exhibition':
    case 'study-abroad':
    case 'trip':
    case 'pet':
    case 'health':
    case 'beauty':
    case 'game':
    case 'finance':
    case 'certification':
    case 'club':
    case 'promotion':
    case 'living':
    case 'women-studies':
      return [ROLE.user, ROLE.admin, ROLE.official];
    // 리자·공식계정만 작성 가능 (공지사항·공식 게시판)
    case 'notice':
    case 'student-council':
    case 'finance-audit':
    case 'graduation-preparation':
      return [ROLE.admin, ROLE.official];
    // 그 외 게시판(베숙트·전체 등)은 아무도 작성할 수 없음
    default:
      return [];
  }
};

const boardPaths = [
  'first-snow',
  'large-snow',
  'permanent-snow',
  'besookt',
  'all',
  'notice',
  'student-council',
  'graduation-preparation',
  'finance-audit',
  'residence',
  'sookplace',
  'certification',
  'video-content',
  'anime-comics',
  'book',
  'music',
  'performance',
  'exhibition',
  'trip',
  'pet',
  'health',
  'cooking',
  'finance',
  'beauty',
  'sport',
  'game',
  'study-abroad',
];

const createBoardRoutes = (paths, feature, forceOff = false) => {
  // 플래그가 없는 게시판은 가드 없이 그대로 렌더한다
  const withFeatureGuard = (element) =>
    feature ? (
      <FeatureGuard feature={feature} fallbackTo='/board' forceOff={forceOff}>
        {element}
      </FeatureGuard>
    ) : (
      element
    );

  return paths.flatMap((boardPath) => [
    {
      path: `/board/${boardPath}`,
      element: withFeatureGuard(
        <ProtectedRoute
          roles={getRolesForReadBoard(boardPath)}
          message={'게시판 접근 권한이 없어요'}
        >
          {boardPath === 'notice' ? <NoticeListPage /> : <PostListPage />}
        </ProtectedRoute>
      ),
    },
    {
      path: `/board/${boardPath}/notice`,
      element: withFeatureGuard(
        <ProtectedRoute
          roles={getRolesForReadBoard(boardPath)}
          message={'게시판 접근 권한이 없어요'}
        >
          <NoticeListPage />
        </ProtectedRoute>
      ),
    },
    {
      path: `/board/${boardPath}/post/:postId`,
      element: withFeatureGuard(
        <ProtectedRoute
          roles={getRolesForReadBoard(boardPath)}
          message={'게시글 접근 권한이 없어요'}
        >
          <PostDetailPage />
        </ProtectedRoute>
      ),
    },
    {
      path: `/board/${boardPath}/post-write`,
      element: withFeatureGuard(
        <ProtectedRoute
          roles={getRolesForWriteBoard(boardPath)}
          message={'게시글 작성 권한이 없어요'}
        >
          <WritePostPage />
        </ProtectedRoute>
      ),
    },
    {
      path: `/board/${boardPath}/post/:postId/edit`,
      element: withFeatureGuard(
        <ProtectedRoute
          roles={getRolesForWriteBoard(boardPath)}
          message={'게시글 편집 권한이 없어요'}
        >
          <EditPostPage />
        </ProtectedRoute>
      ),
    },
    {
      path: `/board/${boardPath}/search`,
      element: withFeatureGuard(
        <ProtectedRoute
          roles={getRolesForReadBoard(boardPath)}
          message={'게시판 접근 권한이 없어요'}
        >
          <SearchPage />
        </ProtectedRoute>
      ),
    },
  ]);
};

const boardRoutes = [
  ...createBoardRoutes(boardPaths),
  // 피처 플래그로 노출을 제어하는 게시판
  ...createBoardRoutes(
    NEW_BOARD_KEYS,
    FEATURE_FLAG.newBoard,
    NEW_BOARD_KILL_SWITCH
  ),
];

export const routeList = [
  {
    path: '/',
    element: <App />,
    errorElement: (
      <AppLayout>
        <ErrorPage />
      </AppLayout>
    ),
    children: [
      {
        index: true,
        element: (
          <NavbarLayout>
            <MainPage />
          </NavbarLayout>
        ),
      },
      {
        path: '/home',
        element: (
          <NavbarLayout>
            <MainPage />
          </NavbarLayout>
        ),
      },
      {
        path: '/board',
        element: (
          <NavbarLayout>
            <BoardCategoryPage />
          </NavbarLayout>
        ),
      },
      ...boardRoutes,
      {
        path: '/board/studyabroad/*',
        element: (
          <LegacyBoardRedirect
            from='/board/studyabroad'
            to='/board/study-abroad'
          />
        ),
      },
      {
        path: '/board/all/search',
        element: <SearchPage />,
      },

      /* 이벤트 */
      {
        path: '/board/event',
        element: (
          <ProtectedRoute
            roles={[ROLE.user, ROLE.admin, ROLE.advertiser]}
            message={'이벤트 게시판 접근 권한이 없어요'}
          >
            <EventListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: `/board/event/notice`,
        element: (
          <ProtectedRoute
            roles={[ROLE.user, ROLE.admin, ROLE.advertiser]}
            message={'이벤트 게시판 접근 권한이 없어요'}
          >
            <NoticeListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/board/event-notice/post/:postId',
        element: (
          <ProtectedRoute
            roles={[ROLE.user, ROLE.admin, ROLE.advertiser]}
            message={'공지글 접근 권한이 없어요'}
          >
            <PostDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/board/event-notice/post/:postId/edit',
        element: (
          <ProtectedRoute
            roles={[ROLE.admin]}
            message={'공지글 수정 권한이 없어요'}
          >
            <EditPostPage />
          </ProtectedRoute>
        ),
      },
      {
        path: `/board/event/event-post-write`,
        element: (
          <ProtectedRoute
            roles={[ROLE.admin]}
            message={'게시글 작성 권한이 없어요'}
          >
            <WriteEventPage />
          </ProtectedRoute>
        ),
      },
      {
        path: `/board/event/post/:postId/edit`,
        element: (
          <ProtectedRoute
            roles={[ROLE.admin]}
            message={'게시글 편집 권한이 없어요'}
          >
            <EditEventPage />
          </ProtectedRoute>
        ),
      },
      {
        path: `/board/event/post/:postId`,
        element: (
          <ProtectedRoute
            roles={[ROLE.user, ROLE.admin, ROLE.advertiser]}
            message={'게시글 접근 권한이 없어요'}
          >
            <EventPage />
          </ProtectedRoute>
        ),
      },

      /* 시험후기 */
      {
        path: '/board/exam-review',
        element: (
          <ProtectedRoute
            roles={[ROLE.user, ROLE.admin]}
            message={'시험후기 접근 권한이 없어요'}
          >
            <NavbarLayout>
              <ExamReviewListPage />
            </NavbarLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: `/board/exam-review/notice`,
        element: (
          <ProtectedRoute
            roles={[ROLE.user, ROLE.admin]}
            message={'시험후기 접근 권한이 없어요'}
          >
            <NoticeListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/board/exam-review/search',
        element: (
          <ProtectedRoute
            roles={[ROLE.user, ROLE.admin]}
            message={'시험후기 접근 권한이 없어요'}
          >
            <NavbarLayout>
              <ExamReviewListPage />
            </NavbarLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/board/exam-review/post/:postId',
        element: (
          <ProtectedRoute
            roles={[ROLE.user, ROLE.admin]}
            message={'시험후기 접근 권한이 없어요'}
          >
            <ExamReviewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/board/exam-review-notice/post/:postId',
        element: (
          <ProtectedRoute
            roles={[ROLE.user, ROLE.admin]}
            message={'공지글 접근 권한이 없어요'}
          >
            <PostDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/board/exam-review-notice/post/:postId/edit',
        element: (
          <ProtectedRoute
            roles={[ROLE.admin]}
            message={'공지글 수정 권한이 없어요'}
          >
            <EditPostPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/board/exam-review/:postId/edit',
        element: (
          <ProtectedRoute
            roles={[ROLE.user, ROLE.admin]}
            message={'시험후기 수정 권한이 없어요'}
          >
            <EditExamReviewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/board/exam-review-write',
        element: (
          <CheckExamPeriodRoute>
            <ProtectedRoute
              roles={[ROLE.user, ROLE.admin]}
              message={'시험후기 작성 권한이 없어요'}
            >
              <WriteExamReviewPage />
            </ProtectedRoute>
          </CheckExamPeriodRoute>
        ),
      },

      /* 알림 */
      {
        path: '/alert',
        element: (
          <ProtectedRoute>
            <NavbarLayout>
              <AlertPage />
            </NavbarLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/alert/setting',
        element: (
          <ProtectedRoute>
            <AlertSettingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/terms/marketing',
        element: <MarketingTermsPage />,
      },
      {
        path: '/attendance',
        element: (
          <ProtectedRoute>
            <AttendancePage />
          </ProtectedRoute>
        ),
        loader: attendanceLoader,
      },

      /* 문의 및 신고 */
      {
        path: 'support',
        children: [
          {
            path: 'faq',
            element: (
              <ProtectedRoute>
                <FAQPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'inquiry',
        children: [
          {
            path: 'write',
            element: (
              <ProtectedRoute>
                <WriteInquiryPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':postId',
            element: (
              <ProtectedRoute>
                <InquiryDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':postId/edit',
            errorElement: (
              <AppLayout>
                <SupportUpdateErrorPage />
              </AppLayout>
            ),
            element: (
              <ProtectedRoute>
                <EditInquiryPage />
              </ProtectedRoute>
            ),
            loader: inquiryEditLoader,
          },
        ],
      },
      {
        path: 'report',
        children: [
          { index: true, element: <NotFoundPage /> },
          {
            path: 'write/:reportType',
            element: (
              <ProtectedRoute>
                <WriteReportPage />
              </ProtectedRoute>
            ),
            loader: validateReportWriteLoader,
          },
          {
            path: ':postId',
            element: (
              <ProtectedRoute>
                <ReportDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':postId/edit',
            errorElement: (
              <AppLayout>
                <SupportUpdateErrorPage />
              </AppLayout>
            ),
            element: (
              <ProtectedRoute>
                <EditReportPage />
              </ProtectedRoute>
            ),
            loader: reportEditLoader,
          },
        ],
      },

      /* 마이페이지 */
      {
        path: '/my-page',
        element: (
          <ProtectedRoute>
            <NavbarLayout>
              <MyPage />
            </NavbarLayout>
          </ProtectedRoute>
        ),
      },
      {
        path: '/my-page/password',
        element: (
          <ProtectedRoute>
            <ChangePwPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/my-page/edit-info',
        element: (
          <ProtectedRoute>
            <EditProfilePage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/my-page/view-point-list',
        element: (
          <ProtectedRoute>
            <PointLogListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/my-page/delete-account',
        element: (
          <ProtectedRoute>
            <DeleteAccountPage />
          </ProtectedRoute>
        ),
      },

      {
        path: '/my-page/my-post',
        element: (
          <ProtectedRoute>
            <ActivityPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/my-page/comment',
        element: (
          <ProtectedRoute>
            <ActivityPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/my-page/download-exam-review',
        element: (
          <ProtectedRoute>
            <ActivityPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/my-page/inquiry-report',
        element: (
          <ProtectedRoute>
            <ActivityPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/my-page/scrap',
        element: (
          <ProtectedRoute>
            <ActivityPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/my-page/scrap-exam-review',
        element: (
          <ProtectedRoute>
            <ActivityPage />
          </ProtectedRoute>
        ),
      },
      // {
      //   path: '/my-page/inquiry-report',
      //   element: <FAQPage />,
      // },
      {
        path: '/my-page/privacy-policy',
        element: <PrivacyPolicyPage />,
      },
      {
        path: '/my-page/service-policy',
        element: <ServicePolicyPage />,
      },
      {
        path: '/commerce/sales/:saleId',
        element: (
          <ProtectedRoute>
            <SaleDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/commerce/orders',
        element: (
          <ProtectedRoute>
            <OrderListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/commerce/orders/:orderNumber',
        element: (
          <ProtectedRoute>
            <OrderDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/commerce/pickup-display',
        element: <PickupDisplayPage />,
      },
      {
        path: '/about',
        element: <AboutPage />,
      },
      {
        path: '/verify',
        element: (
          <ProtectedRoute
            roles={[ROLE.preUser, ROLE.admin]}
            message='이미 인증을 완료했거나 인증 대상이 아니에요'
          >
            <SnoroseVerifyPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/find-id',
        element: <FindIdPage />,
      },
      {
        path: '/find-pw',
        element: <FindPwPage />,
      },
      {
        path: '/found-id',
        element: <FoundIdPage />,
      },
      {
        path: '/found-pw',
        element: <FoundPwPage />,
      },
      {
        path: '/not-found-id',
        element: <NotFoundIdPage />,
      },
      {
        path: '/not-found-pw',
        element: <NotFoundPwPage />,
      },
      {
        path: '/signup',
        element: <SignUpPage />,
      },
      {
        path: '/signup/success',
        element: <SignUpSuccessPage />,
      },
      {
        path: '/signup/failure',
        element: <SignUpFailurePage />,
      },
      {
        path: '/maintenance',
        element: <MaintenancePage />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];
